import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuizQuestions } from '../../hooks/useQuizQuestions';

interface OnboardingQuizProps {
  role: 'chef';
  onComplete: (answers: Record<number, string[]>) => void;
}

export const OnboardingQuiz: React.FC<OnboardingQuizProps> = ({ role, onComplete }) => {
  const { t } = useTranslation('onboarding');
  // Questions text comes from onboarding.json (per-lang); correct index
  // + emojis come from quizMeta (lang-stable). See useQuizQuestions.
  const questions = useQuizQuestions(role);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const answers = useRef<Record<number, string[]>>({});
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const currentQuestion = questions[currentIndex];
  const correctAnswer = currentQuestion.options[currentQuestion.correct];
  const isLastQuestion = currentIndex === questions.length - 1;

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleSelect = (option: string) => {
    if (showResult) return;
    setSelectedAnswer(option);

    answers.current[currentQuestion.id] = [option];
    const correct = option === correctAnswer;
    setIsCorrect(correct);
    setShowResult(true);

    timerRef.current = setTimeout(() => {
      if (isLastQuestion) {
        onComplete(answers.current);
      } else {
        setCurrentIndex((i) => i + 1);
        setSelectedAnswer(null);
        setShowResult(false);
      }
    }, 2000);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-1 text-center">
        <p className="text-sm text-muted-foreground">
          {t('quiz.progress', { current: currentIndex + 1, total: questions.length })}
        </p>
        <div className="flex justify-center gap-1.5">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`h-2 flex-1 rounded-full ${
                i < currentIndex
                  ? 'bg-brand-primary'
                  : i === currentIndex
                    ? 'bg-brand-primary'
                    : 'bg-muted-foreground/25'
              }`}
            />
          ))}
        </div>
      </div>

      <h2 className="text-center text-lg font-semibold">
        {currentQuestion.question}
      </h2>

      <div className="space-y-3">
        {currentQuestion.options.map((option) => {
          const isSelected = option === selectedAnswer;
          let optionStyle = 'border-border bg-background';
          if (showResult) {
            if (option === correctAnswer) {
              optionStyle = 'border-green-500 bg-green-500/15 dark:bg-green-500/20';
            } else if (isSelected && !isCorrect) {
              optionStyle = 'border-red-500 bg-red-500/15 dark:bg-red-500/20';
            } else {
              optionStyle = 'border-border bg-background opacity-40';
            }
          } else if (isSelected) {
            optionStyle = 'border-brand-primary bg-brand-primary/15 ring-2 ring-brand-primary/30';
          }

          return (
            <button
              key={option}
              type="button"
              disabled={showResult}
              onClick={() => handleSelect(option)}
              className={`flex w-full items-center gap-3 rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition-colors ${optionStyle}`}
            >
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                  isSelected
                    ? 'border-brand-primary bg-brand-primary'
                    : 'border-muted-foreground/40 bg-transparent'
                }`}
              >
                {isSelected && (
                  <span className="block h-2 w-2 rounded-full bg-white" />
                )}
              </span>
              <span>{option}</span>
            </button>
          );
        })}
      </div>

      {showResult && (
        <div className="space-y-2 text-center">
          <div className="text-3xl">
            {isCorrect ? currentQuestion.emoji.correct : currentQuestion.emoji.incorrect}
          </div>
          <p className={`text-sm font-medium ${isCorrect ? 'text-green-600' : 'text-red-600'}`}>
            {isCorrect
              ? t('quiz.feedback.correct')
              : t('quiz.feedback.incorrect', { answer: correctAnswer })}
          </p>
        </div>
      )}

    </div>
  );
};
